import Button from '@atlaskit/button';
import WidthDetector from '@atlaskit/width-detector';
import { when } from 'mobx';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { JoinSiteExperimentConsumerProps, withJoinSiteExperiment } from '../../experiments/join-site';
import { AnalyticsConsumerProps, withAnalytics } from '../../hoc/withAnalytics';
import Store from '../../store';
import BlanketStore from '../../store/blanketStore';
import FeatureFlagsStore from '../../store/featureFlagsStore';
import ProductStore, { Site } from '../../store/productStore';
import UIStore from '../../store/uiStore';
import UserStore from '../../store/userStore';
import { getDomainFromEmail } from '../../util/email';

import CardRow from './CardRow';
import { JoinableProduct } from './JoinableProductCard';
import { getHelpfulLinks, getSiteAdminLink } from './LinkCard/helpfulLinks';
import { getVisibleProductsCount } from './util';

const SitesContainer = styled.div`
  margin-top: 16px;
`;

type InjectedProps = AnalyticsConsumerProps & JoinSiteExperimentConsumerProps;

type Props = {
  blanketStore: BlanketStore;
  containerWidth: number;
  featureFlagsStore: FeatureFlagsStore;
  productStore: ProductStore;
  uiStore: UIStore;
  userStore: UserStore;
} & InjectedProps;

@observer
class ProductScreen extends React.Component<Props> {
  componentDidMount() {
    when(
      () => !this.props.joinSiteExperiment.isLoading,
      () => {
        const { isUserAdmin, joinableProducts, availableProducts } = this.props.productStore;
        void this.props.analytics.page('allProductsScreen', {
          availableProductsCount: availableProducts.length,
          isJoinSiteExperimentEnabled: this.props.joinSiteExperiment.isVariation,
          isUserAdmin,
          joinableProductsCount: joinableProducts.length,
        });
      },
    );
  }

  getLinks = () => {
    const { isUserAdmin } = this.props.productStore;
    return getHelpfulLinks(isUserAdmin);
  };

  handleBackButtonClick = () => {
    this.props.analytics.ui('button', 'clicked', {}, { actionSubjectId: 'backButton' });
    this.props.blanketStore.hideBlanket();
  };

  render() {
    const { joinSiteExperiment } = this.props;
    if (joinSiteExperiment.isVariation) {
      return this.renderJoinSiteExperiment();
    }

    const { containerWidth } = this.props;
    const { flags } = this.props.featureFlagsStore;
    const { joinableProducts, joinableSites, availableProducts, availableSites, isUserAdmin } = this.props.productStore;
    const { mobileMode } = this.props.uiStore;

    const renderSites =
      availableSites.length > 0 ? availableSites : flags['uchi.show-joinable-sites'] ? joinableSites : [];
    const renderProducts =
      availableProducts.length > 0 ? availableProducts : flags['uchi.show-joinable-sites'] ? joinableProducts : [];

    // Edge case: If user goes to full page view and resize the screen so it's big enough to fit all of the apps
    // We need to ensure we don't try to fit more invisible cards than necessary
    const maxCardsPerRow = Math.min(renderProducts.length, getVisibleProductsCount(containerWidth, mobileMode));
    const links = getHelpfulLinks(isUserAdmin);

    return (
      <React.Fragment>
        {availableSites.length > 0 && (
          <CardRow
            action={
              <Button appearance="link" spacing="none" onClick={this.handleBackButtonClick}>
                <FormattedMessage {...messages.backToHomePage} />
              </Button>
            }
            data-test-id="frequent-card-row"
            fullScreen
            header={<FormattedMessage {...messages.productsHeader} />}
            products={renderProducts}
            singleRow
            maxCardsPerRow={maxCardsPerRow}
          />
        )}
        <CardRow
          data-test-id="helpful-links-row"
          header={<FormattedMessage {...messages.linksHeader} />}
          links={links}
          fullScreen
          hideSiteName
          maxCardsPerRow={maxCardsPerRow}
        />
        <SitesContainer>
          {renderSites.map(site => {
            return (
              <CardRow
                data-test-id="site-row"
                fullScreen
                key={`product-row-${site.name}`}
                links={site.adminAccess ? [getSiteAdminLink(site.url)] : []}
                header={site.name}
                products={site.products}
                hideSiteName
                maxCardsPerRow={maxCardsPerRow}
              />
            );
          })}
        </SitesContainer>
      </React.Fragment>
    );
  }

  renderJoinSiteExperiment() {
    const { containerWidth } = this.props;
    const { joinableProducts, joinableSites, availableProducts, availableSites } = this.props.productStore;
    const { email } = this.props.userStore;
    const { mobileMode } = this.props.uiStore;
    const links = this.getLinks();
    const userEmailDomain = getDomainFromEmail(email);

    // Edge case: If user goes to full page view and resize the screen so it's big enough to fit all of the apps
    // We need to ensure we don't try to fit more invisible cards than necessary
    const maxCardsPerRow = Math.min(
      availableProducts.length + joinableProducts.length,
      getVisibleProductsCount(containerWidth, mobileMode),
    );

    // Render Order:
    // 1. Frequently accessed sites
    // 2. Sites an user already has access to, and have more products to join
    // 3. Sites an user has no access, and have products to join,
    // and make sure those already appeared in 2. would not show up here
    return (
      <React.Fragment>
        <CardRow
          action={
            <Button appearance="link" spacing="none" onClick={this.handleBackButtonClick}>
              <FormattedMessage {...messages.backToHomePage} />
            </Button>
          }
          data-test-id="frequent-card-row"
          fullScreen
          header={<FormattedMessage {...messages.productsHeader} />}
          products={availableProducts}
          singleRow
          maxCardsPerRow={maxCardsPerRow}
        />
        {availableSites.map((site: Site) => {
          const joinableProductsToAdd = joinableProducts.filter(
            (product: JoinableProduct) => product.cloudId === site.cloudId,
          );
          return (
            <CardRow
              key={`product-row-${site.name}`}
              data-test-id="site-row"
              fullScreen
              links={site.adminAccess ? [getSiteAdminLink(site.url)] : []}
              header={site.name}
              products={site!.products}
              joinableProducts={joinableProductsToAdd}
              hideSiteName
              maxCardsPerRow={maxCardsPerRow}
            />
          );
        })}
        {joinableSites
          .filter(
            (joinableSite: Site) =>
              !availableSites.find((availableSite: Site) => availableSite.cloudId === joinableSite.cloudId),
          )
          .map((site: Site) => {
            const joinableProductsToAdd = joinableProducts.filter(
              (product: JoinableProduct) => product.siteName === site.name,
            );
            return (
              <CardRow
                key={`product-row-${site.name}`}
                data-test-id="joinable-site-row"
                fullScreen
                links={site.adminAccess ? [getSiteAdminLink(site.url)] : []}
                header={site.name}
                subHeader={
                  userEmailDomain && (
                    <span data-test-id="joinable-site-row-subheader">
                      (
                      <FormattedMessage
                        {...messages.peopleFromSubHeader}
                        values={{
                          domain: <strong>{userEmailDomain}</strong>,
                        }}
                      />
                      )
                    </span>
                  )
                }
                joinableProducts={joinableProductsToAdd}
                hideSiteName
                maxCardsPerRow={maxCardsPerRow}
              />
            );
          })}
        <CardRow
          data-test-id="helpful-links-row"
          header={<FormattedMessage {...messages.linksHeader} />}
          links={links}
          fullScreen
          hideSiteName
          maxCardsPerRow={maxCardsPerRow}
        />
      </React.Fragment>
    );
  }
}

const messages = defineMessages({
  backToHomePage: {
    defaultMessage: 'Back',
    description: 'Return to home page',
    id: 'uchi.backToHomePage',
  },
  linksHeader: {
    defaultMessage: 'Helpful links',
    description: 'Helpful links header',
    id: 'uchi.helpfulLinksHeader',
  },
  peopleFromSubHeader: {
    defaultMessage: 'people from @{domain} work here',
    description: 'Subheader for a new joinable site, to inform the user why it is relevant',
    id: 'uchi.peopleFromSubHeader',
  },
  productsHeader: {
    defaultMessage: 'Frequent',
    description: 'Frequent products header',
    id: 'uchi.frequentHeader',
  },
});

export const InjectedProductScreen = inject(
  ({ blanketStore, featureFlagsStore, productStore, uiStore, userStore }: Store) => ({
    blanketStore,
    featureFlagsStore,
    productStore,
    uiStore,
    userStore,
  }),
)(withAnalytics(withJoinSiteExperiment(ProductScreen)));

export default () => (
  <WidthDetector>{(width?: number) => <InjectedProductScreen containerWidth={width || 0} />}</WidthDetector>
);
